import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

// ── FAQ data (15 questions from V2 content) ───────────────────────────────────
const FAQS = [
  {
    q: 'Which is the best CUET coaching in India?',
    a: 'The best CUET coaching depends on your learning preferences, budget, and the level of academic support you need. Look for an institute that offers experienced faculty, live interactive classes, comprehensive study materials, regular mock tests, personalised mentorship, and performance tracking. BESST\'s CUET Online Coaching combines these essential features to help students prepare effectively and improve their chances of securing admission to their preferred university.',
  },
  {
    q: 'Is online CUET coaching effective?',
    a: 'Yes. Online CUET coaching can be just as effective as offline coaching when it includes live interaction, regular testing, and doubt resolution — the format doesn\'t reduce effectiveness as long as the structure is strong.',
  },
  {
    q: 'Can I crack CUET without coaching?',
    a: "It's possible, especially for highly self-disciplined students, but coaching significantly reduces the risk of preparation gaps by providing structure, mock testing, and expert guidance that's hard to replicate alone.",
  },
  {
    q: 'How much does CUET coaching cost?',
    a: "The cost of CUET coaching varies depending on the institute, course format, duration, and the resources included. Online coaching is generally more affordable than offline coaching while offering greater flexibility and access to expert faculty. BESST's CUET Online Coaching is designed to provide quality education at an affordable price, with live classes, comprehensive study materials, mock tests, and personalised mentorship.",
  },
  {
    q: 'What should you look for when choosing a CUET coaching institute?',
    a: 'Look for an institute offering live classes, regular mock tests, mentorship, and performance tracking — these are the features that most directly affect exam outcomes.',
  },
  {
    q: 'How many months are enough for CUET preparation?',
    a: "Most students benefit from at least 4–6 months of structured preparation, though this depends on the student's starting point and the number of domain subjects they're preparing for.",
  },
  {
    q: 'Does BESST provide live classes?',
    a: 'Yes, BESST conducts live, interactive classes across all CUET sections, alongside recorded sessions for revision.',
  },
  {
    q: 'Are mock tests included in BESST\'s coaching?',
    a: "Yes, BESST's CUET coaching program includes regular full-length and sectional mock tests with detailed performance analytics.",
  },
  {
    q: 'Does BESST provide recorded lectures?',
    a: 'Yes, all live classes are recorded and made available for students to revisit at their own pace.',
  },
  {
    q: 'Is mentorship available in BESST\'s CUET coaching?',
    a: "Yes, BESST provides one-to-one mentorship alongside group classes, with personalised study plans based on each student's progress.",
  },
  {
    q: "Can students from anywhere in India join BESST's CUET Online Coaching?",
    a: "Yes. BESST's CUET Online Coaching is designed for students across India. With live interactive classes, recorded sessions, comprehensive study materials, regular mock tests, and personalised mentorship, students can prepare for CUET from anywhere with an internet connection.",
  },
  {
    q: 'Does BESST provide study materials?',
    a: 'Yes, comprehensive, syllabus-aligned study materials are provided as part of the coaching program.',
  },
  {
    q: 'How are doubts resolved?',
    a: "Through dedicated live doubt-clearing sessions in addition to regular class time, so questions don't go unanswered.",
  },
  {
    q: 'Is there a demo class available?',
    a: "Prospective students can enquire about a demo class to experience BESST's teaching style before enrolling.",
  },
  {
    q: "How can I enrol in BESST's CUET Online Coaching?",
    a: "Students can enrol by registering through BESST's CUET Coaching page, after which a counselling session helps determine the right course and study plan.",
  },
];

const FEATURES = [
  { icon: '🎓', title: 'Experienced Faculty', desc: "BESST's faculty bring focused, exam-specific teaching experience in CUET's language, domain, and general test sections — not a repurposed board-exam curriculum." },
  { icon: '📡', title: 'Live Interactive Classes', desc: 'Classes are conducted live, so students can ask questions in real time and stay engaged rather than passively watching pre-recorded content.' },
  { icon: '📚', title: 'Comprehensive Study Materials', desc: 'Structured notes, practice sheets, and topic-wise material aligned directly to the CUET syllabus, so students aren\'t left hunting for resources elsewhere.' },
  { icon: '📝', title: 'Regular Mock Tests', desc: 'Full-length and sectional mock tests run on a set schedule throughout the program, building genuine exam-day familiarity well ahead of time.' },
  { icon: '📄', title: 'Previous Year Question Paper Practice', desc: "Dedicated PYQ practice sessions help students recognise NTA's question patterns and commonly tested areas." },
  { icon: '📊', title: 'Performance Analytics', desc: 'Every mock test feeds into a performance report, so students and mentors can see exactly which sections and topics need more attention.' },
  { icon: '🤝', title: 'One-to-One Mentorship', desc: 'Beyond group classes, students get individual mentorship to work through their specific weak areas and exam strategy.' },
  { icon: '🗓️', title: 'Personalised Study Plans', desc: "Study plans are built around each student's timeline, current level, and target universities — not a one-size-fits-all schedule." },
  { icon: '💬', title: 'Doubt Clearing Sessions', desc: 'Dedicated sessions outside regular class time ensure no doubt goes unresolved for long.' },
  { icon: '🏫', title: 'Career & Admission Guidance', desc: "Support doesn't stop at the exam — BESST also guides students through university and course selection based on their CUET performance." },
  { icon: '💰', title: 'Affordable Fee Structure', desc: 'Built to be accessible for CUET aspirants, without compromising on faculty quality or resources.' },
];

const STEPS = [
  { n: '01', title: 'Register', desc: 'Register for the CUET Online Coaching Program.' },
  { n: '02', title: 'Counselling Session', desc: "Understand the student's current level, target universities, and timeline." },
  { n: '03', title: 'Course Selection', desc: 'Choose subjects and stream based on your target.' },
  { n: '04', title: 'Live Classes', desc: 'Attend live classes covering the full CUET syllabus section by section.' },
  { n: '05', title: 'Daily Practice', desc: 'Reinforce what\'s covered in class with daily exercises.' },
  { n: '06', title: 'Weekly Tests', desc: 'Track ongoing progress with weekly assessments.' },
  { n: '07', title: 'Full-Length Mock Tests', desc: 'Simulate the real exam experience with full-length mocks.' },
  { n: '08', title: 'Performance Review', desc: 'Detailed analytics after every test pinpoint what needs work.' },
  { n: '09', title: 'Final Revision', desc: 'Targeted revision on weak areas identified through testing.' },
  { n: '10', title: 'CUET Examination', desc: 'Walk in prepared, having already faced the format dozens of times.' },
];

const TOC = [
  { id: 'what-is', label: 'What is CUET Coaching?' },
  { id: 'why-important', label: 'Why is it Important?' },
  { id: 'why-online', label: 'Why Choose Online?' },
  { id: 'why-besst', label: 'Why BESST?' },
  { id: 'features-table', label: 'Features' },
  { id: 'what-learn', label: 'What You Will Learn' },
  { id: 'who-should', label: 'Who Should Join?' },
  { id: 'how-works', label: 'How It Works' },
  { id: 'success-tips', label: 'Success Tips' },
  { id: 'faqs', label: 'FAQs' },
];

// ── Schema.org structured data ────────────────────────────────────────────────
const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map(f => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://besst.in' },
    { '@type': 'ListItem', position: 2, name: 'CUET Coaching', item: 'https://besst.in/cuet-coaching' },
  ],
};

const courseSchema = {
  '@context': 'https://schema.org',
  '@type': 'Course',
  name: 'BESST CUET Online Coaching Program',
  description: "BESST's CUET Online Coaching combines expert faculty, regular mock tests, and personalised mentorship to help students crack CUET.",
  provider: {
    '@type': 'Organization',
    name: 'BESST',
    sameAs: 'https://besst.in',
  },
  educationalLevel: 'undergraduate entrance exam preparation',
  teaches: 'CUET — Language Tests, Domain Subjects, General Test',
};

// ── Reusable section component ────────────────────────────────────────────────
const Section = ({ id, children, className = '' }) => (
  <section id={id} className={`cuet-section ${className}`}>{children}</section>
);

const SectionHeading = ({ children }) => (
  <h2 className="cuet-h2">{children}</h2>
);

// ── Main Component ────────────────────────────────────────────────────────────
const CuetCoachingPage = () => {
  const [activeSection, setActiveSection] = useState('what-is');
  const [openFaq, setOpenFaq] = useState(null);
  const observerRef = useRef(null);

  // Scroll spy
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      entries => {
        entries.forEach(e => { if (e.isIntersecting) setActiveSection(e.target.id); });
      },
      { rootMargin: '-30% 0px -60% 0px' }
    );
    TOC.forEach(t => {
      const el = document.getElementById(t.id);
      if (el) observerRef.current.observe(el);
    });
    return () => observerRef.current?.disconnect();
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <Helmet>
        <title>CUET Coaching | Best CUET Online Coaching by BESST</title>
        <meta name="description" content="Join BESST's CUET Online Coaching for expert faculty, live classes, mock tests, study materials, and personalised mentorship to crack CUET with confidence" />
        <meta name="keywords" content="cuet coaching, cuet online coaching, best cuet coaching, cuet coaching classes, online cuet coaching classes, best online coaching for cuet, cuet preparation online, cuet coaching institute, cuet entrance coaching" />
        <meta property="og:title" content="CUET Coaching | Best CUET Online Coaching by BESST" />
        <meta property="og:description" content="Join BESST's CUET Online Coaching for expert faculty, live classes, mock tests, study materials, and personalised mentorship to crack CUET with confidence" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://besst.in/cuet-coaching" />
        <link rel="canonical" href="https://besst.in/cuet-coaching" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(courseSchema)}</script>
      </Helmet>

      <div className="cuet-page mt-20">
        <style>{`
          /* ── Base ───────────────────────────────────────────────────────── */
          .cuet-page {
            background: linear-gradient(160deg, #0c0420 0%, #140831 50%, #0c0420 100%);
            min-height: 100vh;
            color: #e8deff;
            font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;
          }

          /* ── Breadcrumb ─────────────────────────────────────────────────── */
          .cuet-breadcrumb {
            max-width: 1200px;
            margin: 0 auto;
            padding: 1rem 1.5rem 0;
            font-size: 0.8rem;
            color: #9ca3af;
            display: flex;
            align-items: center;
            gap: 0.4rem;
            flex-wrap: wrap;
          }
          .cuet-breadcrumb a { color: #a78bfa; text-decoration: none; }
          .cuet-breadcrumb a:hover { color: #d4af37; }

          /* ── Hero ───────────────────────────────────────────────────────── */
          .cuet-hero {
            padding: 3rem 1.5rem 4rem;
            max-width: 860px;
            margin: 0 auto;
            text-align: center;
          }
          .cuet-hero-badge {
            display: inline-block;
            background: rgba(212,175,55,0.12);
            color: #d4af37;
            border: 1px solid rgba(212,175,55,0.3);
            border-radius: 20px;
            padding: 0.35rem 1rem;
            font-size: 0.78rem;
            font-weight: 600;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            margin-bottom: 1.5rem;
          }
          .cuet-hero h1 {
            font-size: clamp(1.9rem, 4.5vw, 2.9rem);
            font-weight: 800;
            line-height: 1.2;
            margin: 0 0 1.25rem;
            color: #f0e6ff;
          }
          .cuet-hero h1 em {
            font-style: normal;
            background: linear-gradient(90deg, #d4af37, #f59e0b);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }
          .cuet-hero-intro {
            font-size: 1.08rem;
            line-height: 1.75;
            color: #c4b5fd;
            max-width: 720px;
            margin: 0 auto 2rem;
          }
          .cuet-hero-cta {
            display: flex;
            gap: 1rem;
            justify-content: center;
            flex-wrap: wrap;
          }
          .btn-primary-gold {
            background: linear-gradient(135deg, #d4af37, #f59e0b);
            color: #0c0420;
            font-weight: 700;
            padding: 0.85rem 2rem;
            border-radius: 10px;
            border: none;
            cursor: pointer;
            font-size: 0.95rem;
            text-decoration: none;
            display: inline-block;
            transition: transform 0.2s, box-shadow 0.2s;
          }
          .btn-primary-gold:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(212,175,55,0.35); }
          .btn-outline-light {
            background: transparent;
            color: #e8deff;
            font-weight: 600;
            padding: 0.85rem 2rem;
            border-radius: 10px;
            border: 1.5px solid rgba(232,222,255,0.25);
            cursor: pointer;
            font-size: 0.95rem;
            text-decoration: none;
            display: inline-block;
            transition: border-color 0.2s, background 0.2s;
          }
          .btn-outline-light:hover { border-color: #a78bfa; background: rgba(167,139,250,0.08); }

          /* ── Stats banner ───────────────────────────────────────────────── */
          .cuet-stats {
            background: rgba(255,255,255,0.04);
            border-top: 1px solid rgba(255,255,255,0.06);
            border-bottom: 1px solid rgba(255,255,255,0.06);
            padding: 1.5rem 1.5rem;
          }
          .cuet-stats-inner {
            max-width: 1000px;
            margin: 0 auto;
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 1rem;
            text-align: center;
          }
          @media (max-width: 640px) { .cuet-stats-inner { grid-template-columns: repeat(2, 1fr); } }
          .cuet-stat-value { font-size: 1.7rem; font-weight: 800; color: #d4af37; }
          .cuet-stat-label { font-size: 0.78rem; color: #9ca3af; margin-top: 2px; }

          /* ── Layout with sidebar ────────────────────────────────────────── */
          .cuet-layout {
            max-width: 1200px;
            margin: 0 auto;
            padding: 3rem 1.5rem;
            display: grid;
            grid-template-columns: 220px 1fr;
            gap: 3rem;
            align-items: start;
          }
          @media (max-width: 900px) { .cuet-layout { grid-template-columns: 1fr; } }

          /* ── TOC sidebar ────────────────────────────────────────────────── */
          .cuet-toc {
            position: sticky;
            top: 80px;
          }
          @media (max-width: 900px) { .cuet-toc { display: none; } }
          .cuet-toc-title {
            font-size: 0.72rem;
            font-weight: 700;
            letter-spacing: 0.1em;
            text-transform: uppercase;
            color: #7c6b9e;
            margin-bottom: 0.75rem;
          }
          .cuet-toc-list { list-style: none; padding: 0; margin: 0; }
          .cuet-toc-list li { margin-bottom: 0.15rem; }
          .cuet-toc-list button {
            background: none;
            border: none;
            cursor: pointer;
            text-align: left;
            width: 100%;
            padding: 0.4rem 0.6rem;
            font-size: 0.82rem;
            color: #9ca3af;
            border-radius: 6px;
            border-left: 2px solid transparent;
            transition: all 0.15s;
            line-height: 1.4;
          }
          .cuet-toc-list button:hover { color: #e8deff; background: rgba(255,255,255,0.05); }
          .cuet-toc-list button.active { color: #d4af37; border-left-color: #d4af37; background: rgba(212,175,55,0.07); font-weight: 600; }

          /* ── Content sections ───────────────────────────────────────────── */
          .cuet-section { margin-bottom: 3.5rem; scroll-margin-top: 90px; }
          .cuet-h2 {
            font-size: clamp(1.3rem, 2.5vw, 1.7rem);
            font-weight: 700;
            color: #f0e6ff;
            margin: 0 0 1.25rem;
            padding-bottom: 0.6rem;
            border-bottom: 1px solid rgba(212,175,55,0.2);
          }
          .cuet-h3 { font-size: 1.05rem; font-weight: 700; color: #d4af37; margin: 1.25rem 0 0.5rem; }
          .cuet-p { font-size: 0.97rem; line-height: 1.8; color: #c4b5fd; margin: 0 0 1rem; }

          /* ── Importance bullets ─────────────────────────────────────────── */
          .cuet-bullets { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 0.65rem; }
          .cuet-bullets li {
            display: flex;
            gap: 0.75rem;
            align-items: flex-start;
            font-size: 0.95rem;
            line-height: 1.65;
            color: #c4b5fd;
          }
          .cuet-bullet-icon {
            width: 22px;
            height: 22px;
            background: rgba(212,175,55,0.15);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 0.7rem;
            color: #d4af37;
            shrink: 0;
            flex-shrink: 0;
            margin-top: 2px;
          }
          .cuet-bullet-strong { color: #e8deff; font-weight: 600; }

          /* ── Online coaching cards ──────────────────────────────────────── */
          .cuet-online-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 1rem;
          }
          @media (max-width: 600px) { .cuet-online-grid { grid-template-columns: 1fr; } }
          .cuet-online-card {
            background: rgba(255,255,255,0.04);
            border: 1px solid rgba(255,255,255,0.08);
            border-radius: 12px;
            padding: 1.25rem 1.25rem;
            transition: border-color 0.2s;
          }
          .cuet-online-card:hover { border-color: rgba(212,175,55,0.3); }
          .cuet-online-card-icon { font-size: 1.5rem; margin-bottom: 0.5rem; }
          .cuet-online-card h3 { font-size: 0.95rem; font-weight: 700; color: #f0e6ff; margin: 0 0 0.4rem; }
          .cuet-online-card p { font-size: 0.88rem; line-height: 1.6; color: #9ca3af; margin: 0; }

          /* ── BESST features grid ────────────────────────────────────────── */
          .cuet-features-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 0.85rem;
          }
          @media (max-width: 640px) { .cuet-features-grid { grid-template-columns: 1fr; } }
          .cuet-feature-card {
            display: flex;
            gap: 0.85rem;
            align-items: flex-start;
            background: rgba(255,255,255,0.03);
            border: 1px solid rgba(255,255,255,0.07);
            border-radius: 10px;
            padding: 1rem 1.1rem;
            transition: border-color 0.2s, background 0.2s;
          }
          .cuet-feature-card:hover { border-color: rgba(212,175,55,0.25); background: rgba(255,255,255,0.05); }
          .cuet-feature-icon {
            font-size: 1.4rem;
            width: 42px;
            height: 42px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(212,175,55,0.1);
            border-radius: 8px;
            flex-shrink: 0;
          }
          .cuet-feature-card h3 { font-size: 0.88rem; font-weight: 700; color: #f0e6ff; margin: 0 0 0.3rem; }
          .cuet-feature-card p { font-size: 0.83rem; line-height: 1.55; color: #9ca3af; margin: 0; }

          /* ── Feature table ──────────────────────────────────────────────── */
          .cuet-table-wrap { overflow-x: auto; border-radius: 12px; }
          .cuet-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 0.9rem;
          }
          .cuet-table thead tr { background: rgba(212,175,55,0.12); }
          .cuet-table th {
            padding: 0.85rem 1.25rem;
            text-align: left;
            font-weight: 700;
            color: #d4af37;
            border: 1px solid rgba(212,175,55,0.15);
            font-size: 0.85rem;
            letter-spacing: 0.04em;
          }
          .cuet-table td {
            padding: 0.75rem 1.25rem;
            border: 1px solid rgba(255,255,255,0.06);
            color: #c4b5fd;
          }
          .cuet-table tbody tr:nth-child(even) { background: rgba(255,255,255,0.025); }
          .cuet-table tbody tr:hover { background: rgba(212,175,55,0.05); }
          .cuet-tick { color: #22c55e; font-size: 1.1rem; font-weight: 700; }

          /* ── What you learn ─────────────────────────────────────────────── */
          .cuet-learn-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 1rem;
          }
          @media (max-width: 680px) { .cuet-learn-grid { grid-template-columns: 1fr; } }
          .cuet-learn-card {
            background: rgba(124,58,237,0.08);
            border: 1px solid rgba(124,58,237,0.2);
            border-radius: 12px;
            padding: 1.25rem;
          }
          .cuet-learn-card h3 { font-size: 0.95rem; font-weight: 700; color: #a78bfa; margin: 0 0 0.5rem; }
          .cuet-learn-card p { font-size: 0.87rem; line-height: 1.6; color: #9ca3af; margin: 0; }

          /* ── Journey steps ──────────────────────────────────────────────── */
          .cuet-steps { display: flex; flex-direction: column; gap: 0; }
          .cuet-step { display: flex; gap: 1.25rem; align-items: flex-start; position: relative; }
          .cuet-step-left { display: flex; flex-direction: column; align-items: center; flex-shrink: 0; }
          .cuet-step-num {
            width: 44px;
            height: 44px;
            border-radius: 50%;
            background: linear-gradient(135deg, rgba(212,175,55,0.25), rgba(212,175,55,0.1));
            border: 2px solid rgba(212,175,55,0.4);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 0.78rem;
            font-weight: 800;
            color: #d4af37;
            flex-shrink: 0;
          }
          .cuet-step-line {
            width: 2px;
            flex: 1;
            min-height: 28px;
            background: linear-gradient(to bottom, rgba(212,175,55,0.3), rgba(212,175,55,0.05));
          }
          .cuet-step:last-child .cuet-step-line { display: none; }
          .cuet-step-body { padding: 0.6rem 0 1.5rem; }
          .cuet-step-body h3 { font-size: 0.97rem; font-weight: 700; color: #f0e6ff; margin: 0 0 0.25rem; }
          .cuet-step-body p { font-size: 0.87rem; line-height: 1.6; color: #9ca3af; margin: 0; }

          /* ── Tips ───────────────────────────────────────────────────────── */
          .cuet-tips-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 0.65rem;
          }
          @media (max-width: 600px) { .cuet-tips-grid { grid-template-columns: 1fr; } }
          .cuet-tip {
            display: flex;
            gap: 0.65rem;
            align-items: flex-start;
            background: rgba(255,255,255,0.03);
            border-radius: 8px;
            padding: 0.75rem 0.9rem;
            font-size: 0.88rem;
            line-height: 1.55;
            color: #c4b5fd;
            border: 1px solid rgba(255,255,255,0.06);
          }
          .cuet-tip-check { color: #22c55e; font-size: 1rem; flex-shrink: 0; margin-top: 1px; }

          /* ── FAQ ────────────────────────────────────────────────────────── */
          .cuet-faq-list { display: flex; flex-direction: column; gap: 0.6rem; }
          .cuet-faq-item {
            background: rgba(255,255,255,0.03);
            border: 1px solid rgba(255,255,255,0.08);
            border-radius: 10px;
            overflow: hidden;
            transition: border-color 0.2s;
          }
          .cuet-faq-item.open { border-color: rgba(212,175,55,0.3); }
          .cuet-faq-q {
            width: 100%;
            background: none;
            border: none;
            cursor: pointer;
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 1rem;
            padding: 1rem 1.25rem;
            text-align: left;
            font-size: 0.93rem;
            font-weight: 600;
            color: #e8deff;
            transition: color 0.15s;
          }
          .cuet-faq-q:hover { color: #d4af37; }
          .cuet-faq-chevron {
            font-size: 0.75rem;
            color: #7c6b9e;
            transition: transform 0.25s;
            flex-shrink: 0;
          }
          .cuet-faq-item.open .cuet-faq-chevron { transform: rotate(180deg); color: #d4af37; }
          .cuet-faq-a {
            max-height: 0;
            overflow: hidden;
            transition: max-height 0.3s ease, padding 0.3s;
          }
          .cuet-faq-item.open .cuet-faq-a { max-height: 500px; }
          .cuet-faq-a-inner {
            padding: 0 1.25rem 1rem;
            font-size: 0.88rem;
            line-height: 1.75;
            color: #9ca3af;
            border-top: 1px solid rgba(255,255,255,0.06);
            padding-top: 0.85rem;
          }

          /* ── Internal links ─────────────────────────────────────────────── */
          .cuet-links-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 0.65rem;
          }
          @media (max-width: 540px) { .cuet-links-grid { grid-template-columns: 1fr; } }
          .cuet-int-link {
            display: flex;
            align-items: center;
            gap: 0.6rem;
            background: rgba(255,255,255,0.03);
            border: 1px solid rgba(255,255,255,0.07);
            border-radius: 8px;
            padding: 0.7rem 1rem;
            text-decoration: none;
            color: #a78bfa;
            font-size: 0.88rem;
            font-weight: 500;
            transition: border-color 0.2s, background 0.2s;
          }
          .cuet-int-link:hover { border-color: rgba(167,139,250,0.4); background: rgba(167,139,250,0.06); color: #c4b5fd; }

          /* ── CTA banner ─────────────────────────────────────────────────── */
          .cuet-cta-banner {
            background: linear-gradient(135deg, rgba(124,58,237,0.25) 0%, rgba(212,175,55,0.12) 100%);
            border: 1px solid rgba(212,175,55,0.2);
            border-radius: 16px;
            padding: 2.5rem;
            text-align: center;
            margin-top: 1rem;
          }
          .cuet-cta-banner h2 { font-size: 1.5rem; font-weight: 800; color: #f0e6ff; margin: 0 0 0.75rem; }
          .cuet-cta-banner p { font-size: 0.95rem; line-height: 1.7; color: #c4b5fd; max-width: 600px; margin: 0 auto 1.5rem; }
        `}</style>

        {/* Breadcrumb */}
        <nav className="cuet-breadcrumb" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          <span>›</span>
          <span style={{ color: '#e8deff' }}>CUET Coaching</span>
        </nav>

        {/* ── Hero ──────────────────────────────────────────────────────────── */}
        <div className="cuet-hero">
          <div className="cuet-hero-badge">CUET Online Coaching 2025–26</div>
          <h1>
            CUET Coaching: Choose the Best <em>CUET Online Coaching</em> to Crack CUET with Confidence
          </h1>
          <p className="cuet-hero-intro">
            CUET coaching is structured exam preparation — live classes, study materials, mock tests, and mentorship — designed to help students crack the Common University Entrance Test. BESST's CUET online coaching combines expert faculty, regular mock tests, and personalised mentorship for CUET aspirants and to help them secure admission into their dream central university.
          </p>
          <div className="cuet-hero-cta">
            <Link to="/subscriptions" className="btn-primary-gold">Explore Coaching Program →</Link>
            <Link to="/cuet-mock-test" className="btn-outline-light">Try a Free Mock Test</Link>
          </div>
        </div>

        {/* ── Stats bar ──────────────────────────────────────────────────────── */}
        <div className="cuet-stats">
          <div className="cuet-stats-inner">
            <div><div className="cuet-stat-value">10+</div><div className="cuet-stat-label">Features in Every Plan</div></div>
            <div><div className="cuet-stat-value">4–6</div><div className="cuet-stat-label">Months Structured Prep</div></div>
            <div><div className="cuet-stat-value">15+</div><div className="cuet-stat-label">Domain Subjects Covered</div></div>
            <div><div className="cuet-stat-value">Live</div><div className="cuet-stat-label">Interactive Classes</div></div>
          </div>
        </div>

        {/* ── Main layout: sidebar + content ──────────────────────────────────── */}
        <div className="cuet-layout">

          {/* TOC Sidebar */}
          <aside className="cuet-toc" aria-label="Table of contents">
            <div className="cuet-toc-title">On This Page</div>
            <ul className="cuet-toc-list">
              {TOC.map(t => (
                <li key={t.id}>
                  <button className={activeSection === t.id ? 'active' : ''} onClick={() => scrollTo(t.id)}>
                    {t.label}
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          {/* Main content */}
          <main>

            {/* ── What is CUET Coaching ──────────────────────────────────────── */}
            <Section id="what-is">
              <SectionHeading>What is CUET Coaching?</SectionHeading>
              <p className="cuet-p">
                CUET coaching refers to structured, exam-focused preparation for the Common University Entrance Test — typically delivered through coaching classes, study material, practice tests, and guidance from experienced faculty. Instead of a student trying to piece together a preparation strategy from scattered resources, CUET coaching classes package the syllabus, practice, and doubt-solving into one coherent system.
              </p>
              <p className="cuet-p">
                Students choose coaching for a fairly simple reason: CUET rewards consistency and exam-specific technique as much as subject knowledge. A student can know their NCERT syllabus well and still underperform because they weren't used to the time pressure, the negative marking, or the exact question format NTA uses. Coaching closes that gap by simulating the real exam environment repeatedly, well before the actual test day.
              </p>

              <h3 className="cuet-h3">Offline vs Online Coaching</h3>
              <p className="cuet-p">
                Traditional offline coaching institutes work well for students who live near a coaching hub and want in-person interaction. But for students in Tier II and Tier III cities, offline options are often limited, expensive, or simply unavailable within a reasonable distance. Online CUET coaching removes that barrier entirely — the same structured classes, faculty access, and mock tests, delivered wherever the student is.
              </p>

              <h3 className="cuet-h3">Why Structured Preparation Matters</h3>
              <p className="cuet-p">
                Self-study without structure tends to drift — a student might spend three weeks on one subject and rush through another right before the exam. Structured coaching enforces a study plan, paces the syllabus against the actual exam date, and builds in regular testing so gaps get caught in October, not the night before the exam in May.
              </p>
            </Section>

            {/* ── Why Important ─────────────────────────────────────────────── */}
            <Section id="why-important">
              <SectionHeading>Why is CUET Coaching Important?</SectionHeading>
              <p className="cuet-p">
                CUET has grown into one of the most competitive undergraduate entrance exams in the country, and a few structural realities make coaching genuinely useful rather than optional extra spend:
              </p>
              <ul className="cuet-bullets">
                {[
                  ['Increasing competition', 'Lakhs of students now compete for limited seats across central universities, and the applicant pool grows every cycle.'],
                  ['Limited preparation time', 'Most students are preparing for CUET on top of board exams, leaving a narrow window to cover an already wide syllabus.'],
                  ['Subject diversity', 'CUET can involve a language test, multiple domain subjects, and a general test — each with its own pattern and question style.'],
                  ['Exam pattern complexity', 'The marking scheme, sectional time limits, and negative marking all require exam-specific familiarity, not just subject knowledge.'],
                  ['Time management', 'Students frequently lose marks not from not knowing an answer, but from running out of time in a section.'],
                  ['Mock test practice', "Repeated, realistic mock testing is one of the strongest predictors of exam-day performance — it's very difficult to replicate this consistently through self-study alone."],
                ].map(([title, text]) => (
                  <li key={title}>
                    <span className="cuet-bullet-icon">✓</span>
                    <span><span className="cuet-bullet-strong">{title}: </span>{text}</span>
                  </li>
                ))}
              </ul>
              <p className="cuet-p" style={{ marginTop: '1rem' }}>
                Coaching institutes that run structured mock test cycles typically see their students report significantly more confidence going into exam day, simply because the exam format stops being unfamiliar.
              </p>
            </Section>

            {/* ── Why Online ────────────────────────────────────────────────── */}
            <Section id="why-online">
              <SectionHeading>Why Choose Online CUET Coaching?</SectionHeading>
              <p className="cuet-p">Online CUET coaching has become the preferred format for a large share of CUET aspirants, and for good reason:</p>
              <div className="cuet-online-grid">
                {[
                  ['🌍', 'Learn from Anywhere', 'No dependence on where the nearest coaching centre happens to be. Students in Guwahati, Silchar, Dibrugarh, or any smaller town can access the same faculty and material as a student in a metro city.'],
                  ['🕐', 'Flexible Learning Schedule', 'Live classes can be attended around board exam revision and other commitments, without the added time cost of commuting.'],
                  ['🎬', 'Recorded Sessions', 'Missed a class, or need to revisit a concept before a mock test? Recorded sessions mean nothing is lost — students can revise at their own pace.'],
                  ['💰', 'Affordable vs Offline Coaching', 'Without the overhead of a physical centre, online coaching is typically priced more accessibly, which matters for families comparing options on a budget.'],
                  ['💬', 'Live Doubt Solving', "Real-time doubt-clearing sessions mean a confusing concept doesn't sit unresolved for weeks."],
                  ['👨‍🏫', 'Access to Expert Faculty', "Online delivery removes the geographic limit on faculty quality — a student doesn't have to settle for whichever institute happens to be nearby."],
                ].map(([icon, title, desc]) => (
                  <div key={title} className="cuet-online-card">
                    <div className="cuet-online-card-icon">{icon}</div>
                    <h3>{title}</h3>
                    <p>{desc}</p>
                  </div>
                ))}
              </div>
            </Section>

            {/* ── Why BESST ─────────────────────────────────────────────────── */}
            <Section id="why-besst">
              <SectionHeading>Why BESST is Among the Best CUET Coaching Institutes</SectionHeading>
              <p className="cuet-p">
                This is where BESST's CUET online coaching is built to stand apart — not as a generic content provider, but as a program designed around what actually moves a CUET score.
              </p>
              <div className="cuet-features-grid">
                {FEATURES.map(f => (
                  <div key={f.title} className="cuet-feature-card">
                    <div className="cuet-feature-icon">{f.icon}</div>
                    <div>
                      <h3>{f.title}</h3>
                      <p>{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: '1.75rem', textAlign: 'center' }}>
                <Link to="/subscriptions" className="btn-primary-gold">Explore BESST's CUET Online Coaching Program →</Link>
              </div>
            </Section>

            {/* ── Features table ────────────────────────────────────────────── */}
            <Section id="features-table">
              <SectionHeading>BESST CUET Online Coaching Features</SectionHeading>
              <div className="cuet-table-wrap">
                <table className="cuet-table">
                  <thead>
                    <tr>
                      <th>Feature</th>
                      <th>BESST</th>
                    </tr>
                  </thead>
                  <tbody>
                    {['Live Classes','Recorded Classes','Expert Faculty','Mock Tests','PYQs','Doubt Sessions','Performance Reports','Mentorship','Career Guidance','Mobile Learning'].map(f => (
                      <tr key={f}>
                        <td>{f}</td>
                        <td><span className="cuet-tick">✔</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>

            {/* ── What you learn ────────────────────────────────────────────── */}
            <Section id="what-learn">
              <SectionHeading>What Will You Learn in BESST's CUET Coaching Classes?</SectionHeading>
              <p className="cuet-p">CUET coaching classes at BESST are structured around every section a student will actually face in the exam:</p>
              <div className="cuet-learn-grid">
                {[
                  ['Language Tests', 'Reading comprehension, vocabulary, and language-specific question patterns, practised in the same format CUET uses.'],
                  ['Domain Subjects', 'Subject-wise coverage aligned to the CUET domain subject syllabus, with focused revision on high-weightage topics.'],
                  ['General Test', 'Coverage of general knowledge, current affairs, and reasoning as tested in the CUET General Test.'],
                ].map(([title, desc]) => (
                  <div key={title} className="cuet-learn-card">
                    <h3>{title}</h3>
                    <p>{desc}</p>
                  </div>
                ))}
              </div>
            </Section>

            {/* ── Who should join ───────────────────────────────────────────── */}
            <Section id="who-should">
              <SectionHeading>Who Should Join BESST's CUET Coaching?</SectionHeading>
              <p className="cuet-p">BESST's CUET online coaching is built for:</p>
              <ul className="cuet-bullets">
                {[
                  'Class XII students preparing for CUET alongside their board exams',
                  'Drop-year students who want a focused, second attempt at a stronger CUET score',
                  'Students aiming for Central Universities who need structured, high-intent preparation',
                  'Students preparing alongside Board Exams who need a schedule that respects their limited time',
                  "Working students needing flexible learning who can't commit to fixed in-person class timings",
                ].map(t => (
                  <li key={t}><span className="cuet-bullet-icon">→</span><span>{t}</span></li>
                ))}
              </ul>
            </Section>

            {/* ── How it works ──────────────────────────────────────────────── */}
            <Section id="how-works">
              <SectionHeading>How BESST's CUET Coaching Works</SectionHeading>
              <div className="cuet-steps">
                {STEPS.map(s => (
                  <div key={s.n} className="cuet-step">
                    <div className="cuet-step-left">
                      <div className="cuet-step-num">{s.n}</div>
                      <div className="cuet-step-line" />
                    </div>
                    <div className="cuet-step-body">
                      <h3>{s.title}</h3>
                      <p>{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Section>

            {/* ── Success tips ──────────────────────────────────────────────── */}
            <Section id="success-tips">
              <SectionHeading>Success Tips to Crack CUET</SectionHeading>
              <div className="cuet-tips-grid">
                {[
                  'Follow a fixed study schedule rather than studying reactively',
                  'Solve mock tests regularly, not just in the final weeks',
                  'Analyse mistakes after every test instead of only checking the score',
                  'Revise consistently rather than cramming close to the exam',
                  'Work on both speed and accuracy — CUET penalises slow accuracy as much as fast mistakes',
                  'Focus on NCERT as the base for domain subjects',
                  'Stay updated with current affairs for the General Test section',
                ].map(tip => (
                  <div key={tip} className="cuet-tip">
                    <span className="cuet-tip-check">✓</span>
                    <span>{tip}</span>
                  </div>
                ))}
              </div>
            </Section>

            {/* ── Why students prefer ───────────────────────────────────────── */}
            <Section>
              <SectionHeading>Why Students Prefer BESST for CUET Online Coaching</SectionHeading>
              <ul className="cuet-bullets">
                {[
                  'Affordable pricing built for students across income backgrounds',
                  'Personal mentorship rather than a purely classroom-only model',
                  'Tier I and Tier II city-focused support, with a deep understanding of the unique challenges students face in accessing quality CUET coaching',
                  'Flexible online learning that fits around board exam schedules',
                  'Regular performance tracking so progress is visible, not assumed',
                  'Student-centric approach where the study plan adapts to the student, not the other way around',
                ].map(t => (
                  <li key={t}><span className="cuet-bullet-icon">✓</span><span>{t}</span></li>
                ))}
              </ul>
            </Section>

            {/* ── FAQs ──────────────────────────────────────────────────────── */}
            <Section id="faqs">
              <SectionHeading>Frequently Asked Questions (FAQs)</SectionHeading>
              <div className="cuet-faq-list">
                {FAQS.map((f, i) => (
                  <div key={i} className={`cuet-faq-item ${openFaq === i ? 'open' : ''}`}>
                    <button className="cuet-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                      <span>{f.q}</span>
                      <span className="cuet-faq-chevron">▼</span>
                    </button>
                    <div className="cuet-faq-a" aria-hidden={openFaq !== i}>
                      <div className="cuet-faq-a-inner">{f.a}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Section>

            {/* ── Internal links ────────────────────────────────────────────── */}
            <Section>
              <SectionHeading>Explore More CUET Resources</SectionHeading>
              <div className="cuet-links-grid">
                {[
                  ['/cuet-mock-test', '📝 CUET Mock Test'],
                  ['/cuet-syllabus', '📚 CUET Syllabus'],
                  ['/cuet-exam-pattern', '📋 CUET Exam Pattern'],
                  ['/cuet-registration', '📝 CUET Registration'],
                  ['/cuet-colleges', '🏫 CUET Colleges'],
                  ['/cuet-cutoff', '📊 CUET Cutoff'],
                  ['/cuet-counselling', '🎓 CUET Counselling'],
                  ['/cuet-previous-year-question-papers', '📄 CUET PYQ Papers'],
                  ['/cuet-preparation-strategy', '🎯 CUET Preparation Strategy'],
                  ['/cuet-study-plan', '🗓️ CUET Study Plan'],
                ].map(([to, label]) => (
                  <Link key={to} to={to} className="cuet-int-link">
                    <span>{label}</span>
                    <span style={{ marginLeft: 'auto', opacity: 0.5 }}>→</span>
                  </Link>
                ))}
              </div>
            </Section>

            {/* ── CTA ───────────────────────────────────────────────────────── */}
            <div className="cuet-cta-banner">
              <h2>Start Your CUET Journey with BESST</h2>
              <p>
                CUET rewards students who start early and prepare with structure. BESST's CUET Online Coaching Program is built to simplify that entire process — live classes, complete study material, regular mock tests, and mentorship that keeps students accountable from day one through to results day.
              </p>
              <Link to="/subscriptions" className="btn-primary-gold">Join BESST's CUET Online Coaching →</Link>
            </div>

          </main>
        </div>
      </div>
    </>
  );
};

export default CuetCoachingPage;